import clsx from 'clsx'
import Clickable from 'components/Common/Clickable'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import { rem } from 'polished'
import { useCallback, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import useToggle from 'hooks/useToggle'
import { DMY_DATE_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import moment from 'moment'
import { EmptyArray, max, min } from 'lib/array/arrayUtils'
import type { DatePickerMonthChangeHandler } from 'components/Common/Calendar/DatePicker'
import DatePicker from 'components/Common/Calendar/DatePicker'
import Caption from 'LuxKit/Typography/Caption'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'
import noop from 'lib/function/noop'
import FakeInput from 'components/Common/Form/Input/FakeInput'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { mediaHoverable } from 'lib/theme/mediaQueries'

const Day = styled(Clickable)`
  min-height: ${rem(50)};
  background-color: ${props => props.theme.palette.neutral.default.eight};
  transition: background-color 0.2s;
  border-radius: ${props => props.theme.borderRadius.S};
  border: 1px solid ${props => props.theme.palette.neutral.default.eight};
  position: relative;
  overflow: visible;

  &.selected {
    background-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &:disabled {
    color: ${props => props.theme.palette.neutral.default.four};
  }

  &:not(:disabled):not(.selected) {
    ${mediaHoverable} {
      &:hover {
        background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
      }
    }
    &:focus {
      background-color: ${props => props.theme.palette.highlight.primary.lightBackground};
    }
  }
`

const FakeInputClickable = styled(Clickable)`
  width: 100%;
`

const CheckInOutText = styled(Caption)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  z-index: 1;
`

export type ExperienceDateSelectDateChangeHandler = (date: App.ExperienceAvailabilityDate) => void

interface Props {
  onDateChange?: ExperienceDateSelectDateChangeHandler
  onMonthChange?: (direction: 'forwards' | 'backwards') => void
  availableDates?: Array<App.ExperienceAvailabilityDate>
  selectedDate?: string
  checkInDate?: string
  checkOutDate?: string
  lockStayDatesRange?: boolean
  name?: string
  label?: string
  required?: boolean
  disabled?: boolean
  noValidationSpacing?: boolean
  position?: 'top' | 'bottom'
  checkInLabel?: string | React.ReactNode
  checkOutLabel?: string | React.ReactNode
}

function ExperienceDateSelect(props: Props) {
  const intl = useIntl()
  const defaultDateLabel = intl.formatMessage({ id: '0HfBscduuRp1Z5fooHjz', defaultMessage: 'Date', description: 'Default label for the experience date picker in the calendar picker' })
  const defaultCheckInLabel = intl.formatMessage({ id: 'Rc-g5AwA1cPKNq5PKru6', defaultMessage: 'Check in', description: 'Label shown on calendar for check-in date in the calendar picker' })
  const defaultCheckOutLabel = intl.formatMessage({ id: 'SXdpJOkXg1xkVQHGRpN-', defaultMessage: 'Check out', description: 'Label shown on calendar for check-out date in the calendar picker' })
  const {
    onDateChange = noop,
    selectedDate,
    availableDates = EmptyArray,
    checkInDate,
    checkOutDate,
    lockStayDatesRange = false,
    name,
    noValidationSpacing,
    required,
    disabled,
    label = defaultDateLabel,
    checkInLabel = defaultCheckInLabel,
    checkOutLabel = defaultCheckOutLabel,
  }: Props = props
  const selectRef = useRef<HTMLButtonElement>(null)
  const [open, _, setOpen, setClose] = useToggle()
  const [localValue, setLocalValue] = useState<string | undefined>(selectedDate)
  const trackEvent = useTrackExperienceOfferEvent()

  const onDateSelect = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    const formattedDate = moment(+e.currentTarget.dataset.date!).format(ISO_DATE_FORMAT)
    // The dates are rendered from what's in availableDates, so we can assume this `find` will never return undefined
    const day = availableDates.find(available => available.day === formattedDate)!
    setClose()
    setLocalValue(day.day)
    onDateChange(day)
    trackEvent('DATE_ON_CALENDAR_SELECTED')
  }, [availableDates, setClose, onDateChange]) // eslint-disable-line react-hooks/exhaustive-deps -- trackEvent is stable

  const onMonthChange = useCallback<DatePickerMonthChangeHandler>((direction) => {
    if (direction === 'forwards') {
      return trackEvent('NEXT_MONTH_CLICKED')
    }
    return trackEvent('PREV_MONTH_CLICKED')
  }, []) // eslint-disable-line react-hooks/exhaustive-deps -- trackEvent is stable

  const handleOpen = useCallback(() => {
    if (!open) {
      trackEvent('CALENDAR_OPEN')
    }
    setOpen()
  }, [setOpen, open]) // eslint-disable-line react-hooks/exhaustive-deps -- trackEvent is stable

  const handleClose = useCallback(() => {
    if (open) {
      trackEvent('CALENDAR_CLOSE')
    }
    setClose()
  }, [setOpen, open]) // eslint-disable-line react-hooks/exhaustive-deps -- trackEvent is stable

  const availableDays = useMemo(() => {
    let filteredDates = availableDates.filter(date => !date.soldOut)

    if (lockStayDatesRange && checkInDate && checkOutDate) {
      const minBookableDate = moment(checkInDate).subtract(3, 'days').startOf('day')
      const maxBookableDate = moment(checkOutDate).add(3, 'days').endOf('day')
      filteredDates = filteredDates.filter(date => {
        const dateM = moment(date.day)
        return dateM.isSameOrAfter(minBookableDate) && dateM.isSameOrBefore(maxBookableDate)
      })
    }

    return new Set(filteredDates.map(d => moment(d.day).valueOf()))
  }, [availableDates, checkInDate, checkOutDate, lockStayDatesRange])

  const [minDate, maxDate] = useMemo(() => {
    const allDates = availableDates.map(d => moment(d.day).toDate())
    const checkIn = checkInDate ? moment(checkInDate).toDate() : undefined
    const checkOut = checkInDate ? moment(checkOutDate).toDate() : undefined
    return [
      min([...allDates, checkIn]),
      max([...allDates, checkOut]),
    ]
  }, [availableDates, checkInDate, checkOutDate])

  const [defaultMonth, defaultYear] = useMemo(() => {
    let date = minDate
    if (selectedDate) {
      date = moment(selectedDate).toDate()
    } else if (checkInDate && checkOutDate) {
      date = moment(checkInDate).toDate()
    }

    return [
      date?.getMonth(),
      date?.getFullYear(),
    ]
    // it's default values, we only need to calculate this once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [checkInTimestamp, checkOutTimestamp] = useMemo(() => [
    checkInDate ? moment(checkInDate).valueOf() : undefined,
    checkOutDate ? moment(checkOutDate).valueOf() : undefined,
  ], [checkInDate, checkOutDate])

  const finalDate = selectedDate ?? localValue
  const selectedDateAsTime = useMemo(() => finalDate ? moment(finalDate).valueOf() : undefined, [finalDate])

  const selectDatePlaceholder = intl.formatMessage({
    id: 'gH2RG8usEmv-nG2Y1JJv',
    defaultMessage: 'Select a date',
    description: 'Placeholder text for the experience date picker input',
  })
  const pleaseSelectDateMessage = intl.formatMessage({
    id: 'roWraKdH9yfvUwJlgm-D',
    defaultMessage: 'Please select a date',
    description: 'Validation error message when no date has been selected for the experience',
  })

  return (<div style={{ position: 'relative' }}>
    <HiddenInput
      required={required}
      name={name}
      value={finalDate}
      invalidMessage={pleaseSelectDateMessage}
      disabled={disabled}
    />
    <FakeInputClickable
      onClick={(e) => e.currentTarget.focus()}
      onFocus={handleOpen}
      ref={selectRef}
      disabled={disabled}
    >
      <FakeInput
        startIcon={<LineCalendarIcon />}
        value={finalDate ? moment(finalDate).format(DMY_DATE_FORMAT) : undefined}
        placeholder={selectDatePlaceholder}
        requiredErrorMessage={pleaseSelectDateMessage}
        required
        label={label}
        noValidationSpacing={noValidationSpacing}
        disabled={disabled}
      />
    </FakeInputClickable>
    <DropdownSheet
      size="M"
      triggerRef={selectRef}
      anchorRef={selectRef}
      open={open}
      onClose={handleClose}
      crossAxisShift
    >
      <DatePicker
        min={minDate}
        max={maxDate}
        onMonthChange={onMonthChange}
        defaultMonth={defaultMonth}
        defaultYear={defaultYear}
        dayRender={(day: Date) => {
          const dayTime = day.getTime()
          const disabled = !availableDays.has(dayTime)
          const selected = selectedDateAsTime === dayTime

          return <Day
            key={dayTime}
            data-date={dayTime}
            onClick={onDateSelect}
            disabled={disabled}
            data-testid={!disabled ? 'experience-day' : undefined}
            className={clsx({ selected })}
          >
            {(dayTime === checkInTimestamp || dayTime === checkOutTimestamp) &&
              <CheckInOutText wrap="no-wrap" variant="medium" colour={selected ? 'neutral-eight' : 'neutral-three'}>
                {dayTime === checkInTimestamp && checkInLabel}
                {dayTime === checkOutTimestamp && checkOutLabel}
              </CheckInOutText>}
            <BodyText variant="large" weight="bold">
              {day.getDate()}
            </BodyText>
          </Day>
        }}
      />
    </DropdownSheet>
  </div>
  )
}

export default React.memo(ExperienceDateSelect)
